import { useState, useEffect, useMemo, useReducer } from "react";
import './Overview.css';
import {
  GetBarbers,
  GetEvents,
  PostNotification,
  GetNotifications,
  DeleteNotification,
  GetConfig,
  GetPaymentServiceDetails,
  GetRoster,
  UpdateNotification
} from './Requests';
import { formatMinor } from "../../Components/Reusable/CurrencyFormatter/CurrencyFormatter";
import { Button, Modal } from "react-bootstrap";
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { usePagination } from "../../Components/Reusable/TablePagination/Functions";
import TablePaginationFooter from "../../Components/Reusable/TablePagination/TableFooter";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  notifyError,
  notifySuccess
} from '../../Response/ResposeHandler';
import dayjs from 'dayjs';
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import BackButton from "../../Components/Reusable/BackButton/BackButton";
import CreateIcon from '@mui/icons-material/Create';
import { useTranslation } from 'react-i18next';

const fullDaysOfWeek = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
];

// NOTE: we won't use DaysToDisplay/months; we’ll format the date heading with Intl instead for proper localisation.

const reducer = (state, action) => {
  switch (action.type) {
    case 'addNotesOpen':  return { ...state, addNotesOptions: true };
    case 'addNotesClose': return { ...state, addNotesOptions: false };
    case 'editNotesOpen': return { ...state, editNotesOptions: true };
    case 'editNotesClose':return { ...state, editNotesOptions: false };
    default:              return state;
  }
};
const initialState = false;

const Overview = () => {
  const { t, i18n } = useTranslation('overview');

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState();
  const [note, setNote] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [barbersWithClash, setBarbersWithClash] = useState([]);
  const [config, setConfig] = useState();
  const [paymentServiceDetails, setPaymentServiceDetails] = useState();
  const [roster, setRoster] = useState();
  const [addNotificationLoading, setAddNotificationLoading] = useState(false);
  const [noteId, setNoteId] = useState();
  const [deleteNotificationButtonLoading, setDeleteNotificationButtonLoading] = useState(false);

  useEffect(() => {
    const day = fullDaysOfWeek[new Date().getDay()];
    GetBarbersWrapper(day);
    // eslint-disable-next-line
  }, []);

  const total = roster?.length ?? 0;
  const { page, setPage, pageSize, setPageSize, start, end, canPrev, canNext } = usePagination(total);
  const paged = useMemo(() => (roster ? roster.slice(start, end) : []), [roster, start, end]);

  function GetRosterWrapper() {
    GetRoster(new Date()).then((res) => {
      setRoster(res.data);
    }).catch(() => {
      // optionally show an error toast
    }).finally(() => {
      setIsLoading(false);
    });
  }

  function GetBarbersWrapper(day) {
    setIsLoading(true);
    GetBarbers().then((res) => {
      GetEventsWrapper(res.data);
    }).catch(() => {});
  }

  function GetEventsWrapper(barbers) {
    GetEvents(new Date()).then((res) => {
      const appts = res.data.filter(x => x.eventType === 1);
      setEvents(appts);
      hasOverlap(appts, barbers);
    }).catch(() => {
    }).finally(() => {
      GetDashboardNotificationsWrapper();
    });
  }

  function hasOverlap(events, barbers) {
    setBarbersWithClash([]);
    const barberIds = [];
    if (events.lenth !== 0) {
      barbers.forEach(barber => {
        const barberFilteredEvent = events.filter(x => x.barberId === barber.barberId && x.cancelled === false);
        const sorted = barberFilteredEvent.slice().sort((a, b) => new Date(a.startDateTime) - new Date(b.endDateTime));
        for (let i = 0; i < sorted.length - 1; i++) {
          const currentEnd = new Date(sorted[i].endDateTime);
          const nextStart = new Date(sorted[i + 1].startDateTime);
          if (currentEnd > nextStart) {
            barberIds.push(sorted[i].barberId);
            break;
          }
        }
      });
      setBarbersWithClash(barbers.filter(x => barberIds.includes(x.barberId)).map(a => a.name));
    }
  }

  function GetDashboardNotificationsWrapper() {
    GetNotifications().then((res) => {
      setNotifications(res.data);
    }).catch(() => {
    }).finally(() => {
      GetConfigWrapper();
    });
  }

  function GetConfigWrapper() {
    GetConfig().then((res) => {
      setConfig(res.data);
      
    }).catch(() => {
    }).finally(() => {
      GetPaymentServiceDetailsWrapper();
    });
  }

  function GetPaymentServiceDetailsWrapper() {
    GetPaymentServiceDetails().then((res) => {
      if (res.status === 200) setPaymentServiceDetails(res.data);
    }).finally(() => {
      GetRosterWrapper();
    });
  }

  function DeleteDashboardNotification(id) {
    setDeleteNotificationButtonLoading(true);
    DeleteNotification(id).then(() => {
      GetDashboardNotificationsWrapper();
      notifySuccess(t('toasts.notificationDeleted'));
    }).catch(() => {
      notifyError(t('toasts.deleteNotificationFailed'));
    }).finally(() => {
      setDeleteNotificationButtonLoading(false);
      dispatch({ type: 'editNotesClose' });
      setNote("");
      setNoteId();
    });
  }

  function OnNotesSubmit(e) {
    e.preventDefault();
    if (note !== null || note !== undefined || note !== "") {
      setAddNotificationLoading(true);
      PostNotification(note).then(() => {
        notifySuccess(t('toasts.notificationAdded'));
        setNote("");
        setNoteId();
        dispatch({ type: 'addNotesClose' });
        GetDashboardNotificationsWrapper();
      }).catch(() => {
        notifyError(t('toasts.addNotificationFailed'));
      }).finally(() => {
        setAddNotificationLoading(false);
      });
    }
  }

  function OnNotesUpdate(e) {
    e.preventDefault();
    if (note !== null || note !== undefined || note !== "") {
      setAddNotificationLoading(true);
      UpdateNotification(noteId, note).then(() => {
        notifySuccess(t('toasts.notificationEdited'));
        setNote("");
        setNoteId();
        dispatch({ type: 'editNotesClose' });
        GetDashboardNotificationsWrapper();
      }).catch(() => {
        notifyError(t('toasts.editNotificationFailed'));
      }).finally(() => {
        setAddNotificationLoading(false);
      });
    }
  }

  return (
    <div>
      {isLoading ? (
        <div className="custom-loader-wrapper">
          <div className="custom-loader"></div>
        </div>
      ) : (
        <div>
          <ToastContainer />

          {/* Add Notification Modal */}
          <Modal enforceFocus={false} className="modal fade" show={state.addNotesOptions} centered>
            <div className="sm-modal">
              <div className="sm-modal__header">
                <h5 className="sm-modal__title">{t('modals.add.title')}</h5>
                <Button
                  variant=""
                  type="button"
                  className="btn-close"
                  data-dismiss="modal"
                  onClick={() => { dispatch({ type: 'addNotesClose' }); setNoteId(); setNote(""); }}
                />
              </div>
              <div className="modal-body">
                <form className="comment-form" onSubmit={OnNotesSubmit}>
                  <div className="row">
                    <div className="col-lg-12 mb-3">
                      <div className="sm-field sm-field--spaced">
                        <label className="sm-label">{t('modals.common.label')}</label>
                        <textarea
                          rows={5}
                          className="sm-textarea"
                          defaultValue={note}
                          maxLength={250}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12" style={{ display: "flex", justifyContent: "flex-end" }}>
                      <CustomButton
                        className="app-btn--sm"
                        disabled={note === ""}
                        onClick={(e) => { OnNotesSubmit(e); }}
                        label={t('modals.add.add')}
                        loading={addNotificationLoading}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </Modal>

          {/* Edit Notification Modal */}
          <Modal enforceFocus={false} className="modal fade" show={state.editNotesOptions} centered>
            <div className="sm-modal">
              <div className="sm-modal__header">
                <h5 className="sm-modal__title">{t('modals.edit.title')}</h5>
                <Button
                  variant=""
                  type="button"
                  className="btn-close"
                  data-dismiss="modal"
                  onClick={() => { dispatch({ type: 'editNotesClose' }); setNoteId(); setNote(""); }}
                />
              </div>
              <div className="modal-body">
                <form className="comment-form" onSubmit={OnNotesUpdate}>
                  <div className="row">
                    <div className="col-lg-12 mb-3">
                      <div className="sm-field sm-field--spaced">
                        <label className="sm-label">{t('modals.common.label')}</label>
                        <textarea
                          rows={5}
                          className="sm-textarea"
                          defaultValue={note}
                          maxLength={250}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={{marginLeft:"auto"}} className="sm-actions">
                      <BackButton
                        loading={deleteNotificationButtonLoading}
                        onClick={() => { DeleteDashboardNotification(noteId); }}
                        label={t('modals.edit.delete')}
                        style={{ marginRight: "auto" }}
                      />
                      <CustomButton
                        loading={addNotificationLoading}
                        disabled={note === ""}
                        onClick={(e) => { OnNotesUpdate(e); }}
                        className="app-btn--sm"
                        label={t('modals.edit.edit')}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </Modal>

          {/* Stripe attention banner */}
          {paymentServiceDetails && paymentServiceDetails.requirements && (
            <div className="sched-alert" style={{ textAlign: "center" }}>
              {t('alerts.stripeAttention')}
            </div>
          )}

          {/* Date heading */}
          <div className="dashboard-container">
            <div className="dashboard-card">
              <h1 className="date-title">
                {new Intl.DateTimeFormat(i18n.language || "en", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })
                  .format(new Date()).replace(/,/g, "")}
              </h1>
            </div>
          </div>

          {/* Money cards (money formatting left as-is) */}
          {config.acceptOnlinePayments === true ? (
            <div className="dashboard-container">
              <div className="funds-grid-item">
                <div className="funds-row">
                  <div className="dashboard-card">
                    <h2 className="card-title">{t('cards.availableFunds')}</h2>
                    <div className="big-number">{formatMinor(paymentServiceDetails.availableFunds, config.currency)}</div>
                    <div className="subtext">{t('cards.availableFundsDescription')}</div>
                  </div>
                  
                  <div className="dashboard-card">
                    <h2 className="card-title">{t('cards.pendingFunds')}</h2>
                    <div className="big-number">{formatMinor(paymentServiceDetails.pendingFunds, config.currency)}</div>
                    <div className="subtext">{t('cards.pendingFundsDescription')}</div>
                  </div>
                </div>
              </div>
              </div>
          ) : null}

          {/* Roster table */}
          <div className="dashboard-container" style={{ gridTemplateColumns: "none", paddingBottom: 0, gap: 0 }}>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('table.name')}</th>
                    <th>{t('table.breaks')}</th>
                    <th>{t('table.appointments')}</th>
                    <th style={{ minWidth: "9rem" }}>{t('table.hours')}</th>
                    <th>{t('table.leave')}</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.length === 0 ? null : paged.map((val, index) => (
                    <tr key={index}>
                      <td>
                        {/* {val.staffName} */}

                          <span className="table-field-ellipsis" title={val.staffName}>
                            {val.staffName}
                          </span>
                      </td>
                      <td>{val.breaks}</td>
                      <td>
                        {(events.filter(x => x.barberId === val.staffId)).length}{' '}
                        {events.filter(x => x.barberId === val.staffId & x.isFirstAppointment).length === 0 ? "" :
                          "(" + ((events.filter(x => x.barberId === val.staffId & x.isFirstAppointment)).length) + String.fromCodePoint(9733) + ")"
                        }
                      </td>
                      <td>{val.workingHours}</td>
                      <td>
                        {val.leaveId !== null
                          ? ((new Date(val.startDateTime)).getDate() + "/" + (dayjs(val.startDateTime).month() + 1) + " - " + (new Date(val.endDateTime)).getDate() + "/" + ((dayjs(val.endDateTime)).month() + 1))
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {roster.length > 8 ? (
              <TablePaginationFooter
                total={total}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                canPrev={canPrev}
                canNext={canNext}
              />
            ) : null}
          </div>

          {/* Notifications & overlaps */}
          <div className="dashboard-container" style={{ marginBottom: "1rem" }}>
            <div className="dashboard-card">
              <div style={{ display: "flex" }}>
                <h2 className="h2 card-title">{t('cards.notifications')}</h2>
                <IconButton onClick={() => { dispatch({ type: 'addNotesOpen' }); }} aria-label="add" size="large" style={{ justifyContent: "right", marginLeft: "auto" }}>
                  <AddCircleOutlineIcon style={{ justifyContent: "right" }} />
                </IconButton>
              </div>
              <ul className="notifications-list">
                {notifications.length === 0 ? (
                  <span className="not-working">{t('cards.noNotifications')}</span>
                ) : notifications.map((notification, index) => (
                  <div key={index} style={{ display: "flex" }}>
                    <li style={{ width: "90%", marginTop:"0.5rem" }}>{notification.notification}</li>
                    <IconButton
                      onClick={() => { dispatch({ type: 'editNotesOpen' }); setNote(notification.notification); setNoteId(notification.id); }}
                      style={{ marginLeft: "auto" }}
                      aria-label="edit"
                      size="large"
                    >
                      <CreateIcon />
                    </IconButton>
                  </div>
                ))}
              </ul>
            </div>

            <div className={barbersWithClash.length === 0 ? "dashboard-card" : "background-red dashboard-card"}>
              <div style={{ display: "flex", justifyContent: "center", textAlign: "center" }}>
                <h2 className="h2 card-title">{t('cards.appointmentOverlaps')}</h2>
              </div>
              <ul className="staff-list">
                {barbersWithClash.length === 0 ? (
                  <div style={{ display: "flex", justifyContent: "center", textAlign: "center" }}>
                    <li className="not-working">{t('cards.noOverlaps')}</li>
                  </div>
                ) : barbersWithClash.map((barber, index) => (
                  <li key={index}>{barber}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
